import { useCallback, useEffect, useMemo, useState } from 'react'
import { bettingApi } from '../../api'
import { ErrorState, errorMessage } from '../ErrorState'
import type { AggregatorStatus, Carrier, PaymentAggregator } from '../../types'
import { Block, PageHeader, SidePanel, ViewTabs } from '../../ui/ui'
import { useI18n, useT } from '../../i18n'
import { AGGREGATOR_STATUSES, aggregatorStatusLabel, AggregatorStatusPill, countryLabel, useBettingVocabulary } from './bettingShared'
import { BettingDiscoveryTerms } from './BettingDiscoveryTerms'

export function BettingReferentials({ canManage }: { canManage: boolean }) {
  const t = useT()
  const [aggregators, setAggregators] = useState<PaymentAggregator[]>([])
  const reloadAggregators = useCallback(() => {
    bettingApi.listAggregators().then(setAggregators).catch(() => undefined)
  }, [])
  useEffect(reloadAggregators, [reloadAggregators])

  return (
    <>
      <PageHeader
        title={t('bet.referentialsTitle')}
        description={t('bet.referentialsDesc')}
      />
      <AggregatorsSection aggregators={aggregators} canManage={canManage} onChanged={reloadAggregators} />
      <CarriersSection canManage={canManage} />
      <BettingDiscoveryTerms canManage={canManage} />
    </>
  )
}

// ── Agrégateurs ──────────────────────────────────────────────────────────────

function AggregatorsSection({ aggregators, canManage, onChanged }: { aggregators: PaymentAggregator[]; canManage: boolean; onChanged: () => void }) {
  const { t, fmt } = useI18n()
  const [editing, setEditing] = useState<PaymentAggregator | 'new' | null>(null)
  const [importing, setImporting] = useState(false)
  const [filter, setFilter] = useState<AggregatorStatus | ''>('')
  const shown = aggregators.filter((a) => !filter || a.status === filter)
  const count = (status: AggregatorStatus) => aggregators.filter((a) => a.status === status).length

  return (
    <Block
      flush
      title={t('bet.aggregatorsTitle')}
      subtitle={t('bet.aggregatorsSub', {
        n: aggregators.length,
        authorized: count('AUTHORIZED'),
        unauthorized: count('UNAUTHORIZED'),
        unknown: count('UNKNOWN'),
      })}
      action={
        canManage ? (
          <div className="bet-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setImporting(true)}>{t('bet.importIntouchList')}</button>
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>{t('common.add')}</button>
          </div>
        ) : undefined
      }
    >
      <div className="bet-block-tabs">
        <ViewTabs
          label={t('bet.filterByStatus')}
          value={filter}
          onChange={setFilter}
          options={[
            { value: '', label: t('bet.filterAllM') },
            { value: 'AUTHORIZED', label: t('bet.aggAuthorizedPl') },
            { value: 'UNAUTHORIZED', label: t('bet.aggUnauthorizedPl') },
            { value: 'UNKNOWN', label: t('bet.aggUnknownPl') },
          ]}
        />
      </div>
      <div className="table-scroll">
        <table className="bet-table">
          <thead>
            <tr>
              <th scope="col">{t('bet.colAggregator')}</th>
              <th scope="col">{t('common.status')}</th>
              <th scope="col">{t('common.country')}</th>
              <th scope="col">{t('bet.colKnownDomains')}</th>
              <th scope="col">{t('bet.colSmsNames')}</th>
              {canManage && <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>}
            </tr>
          </thead>
          <tbody>
            {shown.map((a) => (
              <tr key={a.id}>
                <td><strong>{a.name}</strong></td>
                <td><AggregatorStatusPill status={a.status} /></td>
                <td>{a.countries.length ? a.countries.join(', ') : <span className="bet-muted">{t('bet.allCountriesShort')}</span>}</td>
                <td className="bet-mono bet-list-cell">{a.domains.join(', ') || <span className="bet-muted">—</span>}</td>
                <td className="bet-mono bet-list-cell">{a.beneficiaryNames.join(', ') || <span className="bet-muted">—</span>}</td>
                {canManage && (
                  <td><button type="button" className="btn btn-secondary" onClick={() => setEditing(a)}>{t('common.edit')}</button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <AggregatorDialog
          aggregator={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onChanged()
          }}
        />
      )}
      {importing && (
        <ImportDialog
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false)
            onChanged()
          }}
        />
      )}
    </Block>
  )
}

const splitList = (s: string) => s.split(/[,|\n]/).map((v) => v.trim()).filter(Boolean)

function AggregatorDialog({ aggregator, onClose, onSaved }: { aggregator: PaymentAggregator | null; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const [name, setName] = useState(aggregator?.name ?? '')
  const [status, setStatus] = useState<AggregatorStatus>(aggregator?.status ?? 'UNKNOWN')
  const [countries, setCountries] = useState((aggregator?.countries ?? []).join(', '))
  const [domains, setDomains] = useState((aggregator?.domains ?? []).join('\n'))
  const [names, setNames] = useState((aggregator?.beneficiaryNames ?? []).join('\n'))
  const [notes, setNotes] = useState(aggregator?.notes ?? '')
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    const payload = {
      name: name.trim(),
      status,
      countries: splitList(countries).map((c) => c.toUpperCase()),
      domains: splitList(domains),
      beneficiaryNames: splitList(names),
      notes: notes.trim() || null,
    }
    try {
      if (aggregator) await bettingApi.updateAggregator(aggregator.id, payload)
      else await bettingApi.createAggregator(payload)
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <SidePanel
      title={aggregator ? t('bet.aggFormEdit', { name: aggregator.name }) : t('bet.aggFormNew')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={name.trim().length < 2} onClick={submit}>{t('common.save')}</button>
        </>
      }
    >
        <div className="bet-form">
          <div className="bet-form-row">
            <label>
              <span>{t('common.name')}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              <span>{t('common.status')}</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as AggregatorStatus)}>
                {AGGREGATOR_STATUSES.map((s) => (
                  <option key={s} value={s}>{aggregatorStatusLabel(t, s)}</option>
                ))}
              </select>
            </label>
          </div>
          <label>
            <span>{t('bet.aggCountriesField')}</span>
            <input value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="CM, GA" />
          </label>
          <label>
            <span>{t('bet.aggDomainsField')}</span>
            <textarea rows={3} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="cinetpay.com" />
          </label>
          <label>
            <span>{t('bet.aggNamesField')}</span>
            <textarea rows={3} value={names} onChange={(e) => setNames(e.target.value)} placeholder="TECH MINERVE TECH" />
          </label>
          <label>
            <span>{t('common.notes')}</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          {error && <ErrorState message={error} compact />}
        </div>
    </SidePanel>
  )
}

const STATUS_WORDS: Record<string, AggregatorStatus> = {
  AUTHORIZED: 'AUTHORIZED',
  AUTORISE: 'AUTHORIZED',
  OUI: 'AUTHORIZED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  'NON AUTORISE': 'UNAUTHORIZED',
  NON: 'UNAUTHORIZED',
  UNKNOWN: 'UNKNOWN',
  'A QUALIFIER': 'UNKNOWN',
}

/**
 * Lecture du collage.
 *
 * Les mots de statut acceptés restent français : ce sont ceux des fichiers
 * déjà échangés avec les équipes. `AUTHORIZED` et ses pareils marchent aussi.
 */
function parseImport(text: string) {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !/^nom\s*;/i.test(l))
    .map((line, i) => {
      const [name = '', statusRaw = '', countries = '', domains = '', names = ''] = line.split(';').map((c) => c.trim())
      const key = statusRaw.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
      const status = STATUS_WORDS[key]
      return {
        line: i + 1,
        name,
        status,
        countries: splitList(countries).map((c) => c.toUpperCase()),
        domains: splitList(domains),
        beneficiaryNames: splitList(names),
        error: !name ? ('MISSING_NAME' as const) : !status ? ('BAD_STATUS' as const) : null,
        statusRaw,
      }
    })
}

function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const t = useT()
  const [text, setText] = useState('')
  const [result, setResult] = useState<{ created: number; updated: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const rows = useMemo(() => parseImport(text), [text])
  const valid = rows.filter((r) => !r.error)

  const submit = async () => {
    setError(null)
    try {
      setResult(
        await bettingApi.importAggregators(
          valid.map((r) => ({ name: r.name, status: r.status, countries: r.countries, domains: r.domains, beneficiaryNames: r.beneficiaryNames })),
        ),
      )
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <SidePanel
      wide
      title={t('bet.importAggTitle')}
      onClose={onClose}
      footer={
        result ? (
          <button type="button" className="btn btn-primary" onClick={onDone}>{t('common.close')}</button>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-primary" disabled={valid.length === 0} onClick={submit}>
              {t('bet.importAggSubmit', { n: valid.length })}
            </button>
          </>
        )
      }
    >
        <div className="bet-form">
          {result ? (
            <p className="bet-notice" role="status">
              {t('bet.importAggDone', { created: result.created, updated: result.updated })}
            </p>
          ) : (
            <>
              <p className="bet-hint">{t('bet.importAggHelp')}</p>
              <textarea
                rows={7}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="bet-mono"
                placeholder={'nom;statut;pays;domaines;noms dans les SMS\nCinetPay;autorisé;CI,SN;cinetpay.com;CINETPAY\nMinerve Tech;non autorisé;GA,CM;;TECH MINERVE TECH'}
                aria-label={t('bet.importAggAria')}
              />
              {rows.length > 0 && (
                <p className="bet-hint">
                  {t('bet.importAggReady', { n: valid.length })}
                  {rows.length - valid.length > 0 && ` · ${t('bet.importAggIgnored', { n: rows.length - valid.length })}`}
                </p>
              )}
              {rows.some((r) => r.error) && (
                <ul className="bet-import-errors">
                  {rows.filter((r) => r.error).map((r) => (
                    <li key={r.line}>
                      {t('bet.importAggRowError', {
                        line: r.line,
                        error:
                          r.error === 'MISSING_NAME'
                            ? t('bet.importAggNoName')
                            : t('bet.importAggBadStatus', { status: r.statusRaw }),
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {error && <ErrorState message={error} compact />}
        </div>
    </SidePanel>
  )
}

// ── Opérateurs télécom ───────────────────────────────────────────────────────

function CarriersSection({ canManage }: { canManage: boolean }) {
  const t = useT()
  const vocab = useBettingVocabulary()
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [country, setCountry] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    bettingApi.listCarriers().then(setCarriers).catch((err) => setError(errorMessage(err)))
  }, [])
  useEffect(load, [load])

  const byCountry = carriers.reduce<Record<string, Carrier[]>>((acc, c) => {
    ;(acc[c.country] ??= []).push(c)
    return acc
  }, {})

  const add = async () => {
    setError(null)
    try {
      await bettingApi.createCarrier({ name: name.trim(), country })
      setName('')
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const remove = async (c: Carrier) => {
    try {
      await bettingApi.deleteCarrier(c.id)
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <Block
      title={t('bet.carriersTitle')}
      subtitle={t('bet.carriersSub')}
      action={
        canManage ? (
          <div className="bet-actions">
            <select aria-label={t('bet.carrierCountryAria')} value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">{t('common.country')}</option>
              {vocab?.countries.map((c) => <option key={c.code} value={c.code}>{countryLabel(vocab, c.code, t)}</option>)}
            </select>
            <input aria-label={t('bet.carrierNameAria')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('bet.carrierNameAria')} />
            <button type="button" className="btn btn-primary" disabled={!country || name.trim().length < 2} onClick={add}>{t('bet.addCarrier')}</button>
          </div>
        ) : undefined
      }
    >
      {error && <ErrorState message={error} compact />}
      <div className="bet-carriers">
        {Object.keys(byCountry).sort().map((code) => (
          <div key={code} className="bet-carrier-group">
            <span className="chart-title">{countryLabel(vocab, code, t)}</span>
            <ul>
              {byCountry[code].map((c) => (
                <li key={c.id}>
                  {c.name}
                  {canManage && (
                    <button type="button" className="bet-chip-remove" aria-label={t('bet.removeCarrier', { name: c.name })} onClick={() => remove(c)}>&times;</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Block>
  )
}
